import { DEFAULT_BACKEND_URL_PATH } from "../const";

export const DEFAULT_MONTHLY_QUOTA_USD = 100.0;
export const FREE_MONTHLY_QUOTA_USD = DEFAULT_MONTHLY_QUOTA_USD;
export const WARNING_THRESHOLD_PERCENT = 70;
export const ALERT_THRESHOLD_PERCENT = 90;
export const BLOCK_THRESHOLD_PERCENT = 99;

export const AI_USAGE_UPDATED_EVENT = "elelem:ai-usage-updated";

const getBackendUrl = () => {
  return (process.env.REACT_APP_BACKEND_URL || DEFAULT_BACKEND_URL_PATH || "").replace(
    /\/+$/,
    ""
  );
};

/**
 * Format raw token counts cleanly (e.g. 1,250 -> 1.3k, 1,250,000 -> 1.3M).
 */
export const formatTokens = (tokens, { compact = false } = {}) => {
  const num = Number(tokens) || 0;
  if (!compact) {
    return num.toLocaleString();
  }
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  }
  return num.toString();
};

/**
 * Format cost in USD with appropriate decimals.
 */
export const formatUsdCost = (cost) => {
  const num = Number(cost) || 0;
  if (num === 0) return "$0.00";
  if (num < 0.01) {
    return `$${num.toFixed(4)}`;
  }
  return `$${num.toFixed(2)}`;
};

/**
 * Compute quota status based on current USD cost and monthly quota ($100).
 * - normal (< 70%): brand dark teal
 * - warning (>= 70%): orange
 * - danger (>= 90%): red
 * - blocked (>= 99%): critical red
 */
export const calculateQuotaStatus = (
  costUsd,
  quotaUsd = DEFAULT_MONTHLY_QUOTA_USD
) => {
  const cost = Math.max(0, Number(costUsd) || 0);
  const quota = Math.max(0.01, Number(quotaUsd) || DEFAULT_MONTHLY_QUOTA_USD);
  const percentage = Math.min(100, (cost / quota) * 100);
  const remainingUsd = Math.max(0, quota - cost);

  let status = "normal";
  if (percentage >= BLOCK_THRESHOLD_PERCENT) {
    status = "blocked";
  } else if (percentage >= ALERT_THRESHOLD_PERCENT) {
    status = "danger";
  } else if (percentage >= WARNING_THRESHOLD_PERCENT) {
    status = "warning";
  }

  return {
    cost,
    quota,
    percentage: Math.round(percentage * 10) / 10,
    remainingUsd: Math.round(remainingUsd * 100) / 100,
    status,
    isBlocked: percentage >= BLOCK_THRESHOLD_PERCENT,
  };
};

/**
 * Fetch aggregated client AI usage across all users in the active client.
 * Calls GET /api/v1/ai-usage.
 */
export const fetchClientAiUsage = async ({
  clientId,
  startDate,
  endDate,
} = {}) => {
  const backendUrl = getBackendUrl();
  const params = new URLSearchParams();
  if (clientId) params.append("client_id", clientId);
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);

  const qs = params.toString() ? `?${params.toString()}` : "";
  const url = `${backendUrl}/api/v1/ai-usage${qs}`;

  try {
    const res = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    }

    return await res.json();
  } catch (err) {
    console.warn("Failed to fetch client AI usage:", err);
    throw err;
  }
};

/**
 * Fetch detailed AI usage for a specific user, including per-session and per-service breakdown.
 * Calls GET /api/v1/ai-usage/users/{target_username}.
 */
export const fetchUserAiUsage = async (
  username,
  { clientId, startDate, endDate } = {}
) => {
  if (!username) {
    throw new Error("username is required to fetch user AI usage");
  }

  const backendUrl = getBackendUrl();
  const params = new URLSearchParams();
  if (clientId) params.append("client_id", clientId);
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);

  const qs = params.toString() ? `?${params.toString()}` : "";
  const url = `${backendUrl}/api/v1/ai-usage/users/${encodeURIComponent(
    username
  )}${qs}`;

  try {
    const res = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    }

    return await res.json();
  } catch (err) {
    console.warn(`Failed to fetch user AI usage for ${username}:`, err);
    throw err;
  }
};

/**
 * Dispatch global event notifying that AI usage has updated.
 */
export const triggerAiUsageRefresh = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AI_USAGE_UPDATED_EVENT));
  }
};

/**
 * Subscribe to AI usage refresh events.
 */
export const subscribeAiUsageUpdate = (callback) => {
  if (typeof window === "undefined" || typeof callback !== "function") {
    return () => {};
  }
  const handler = () => callback();
  window.addEventListener(AI_USAGE_UPDATED_EVENT, handler);
  return () => {
    window.removeEventListener(AI_USAGE_UPDATED_EVENT, handler);
  };
};
